import { z } from "zod";
import { SMIC_MINIMUM } from "../../utils/remuneration.constants";
import { EmployeeStatusSchema, RemunerationTemplateThirteenthMonthUnitSchema } from "../../types/supabase.types.gen";
import {
  remunerationIndemnityPayloadSchema,
  remunerationProfileIndemnityViewSchema,
} from "./remuneration-profile-indemnity";
import { remunerationProfileOriginSchema } from "./remuneration-profile-data";

/**
 * Fields shared by the profile read model and POST/PATCH body (identical validation).
 * Read and insert diverge on `hourly_rate`, optional hour fields, defaults, and indemnity list shape — see `.extend` below.
 */
export const remunerationProfileBaseSchema = z.object({
  hours_coefficient: z.number().min(1, "Le coefficient heures doit être supérieur ou égal à 1"),
  bonus_coefficient: z.number().min(1, "Le coefficient primes doit être supérieur ou égal à 1"),
  employee_status: EmployeeStatusSchema.nullable(),
  thirteenth_month_unit: RemunerationTemplateThirteenthMonthUnitSchema,
});

/** Profile read model: `remuneration_profile` scalars + nested indemnities (persisted row fields). */
export const remunerationProfileWithIndemnitiesSchema = remunerationProfileBaseSchema.extend({
  id: z.guid(),
  hourly_rate: z.number(),
  thirteenth_month_enabled: z.boolean(),
  thirteenth_month_amount: z.number().min(0).nullable().optional(),
  rtt_enabled: z.boolean(),
  rtt_weekly_hours: z.number().nullable(),
  overtime_enabled: z.boolean(),
  overtime_has_custom_convention: z.boolean().nullable(),
  overtime_rate_36_to_43_percent: z.number().nullable(),
  overtime_rate_above_43_percent: z.number().nullable(),
  indemnities: z.array(remunerationProfileIndemnityViewSchema),
});

export type RemunerationProfileWithIndemnities = z.infer<typeof remunerationProfileWithIndemnitiesSchema>;

/** Profile block for creating a template (POST body `profile` field). Cross-field rules validated in API via shared helpers. */
export const remunerationProfileInsertPayloadSchema = remunerationProfileBaseSchema.extend({
  hourly_rate: z.number().min(SMIC_MINIMUM, `Le taux horaire doit être au moins égal au SMIC (${SMIC_MINIMUM} €)`),
  thirteenth_month_enabled: z.boolean().default(false),
  thirteenth_month_amount: z.number().min(0).optional(),
  rtt_enabled: z.boolean().default(false),
  rtt_weekly_hours: z.number().positive("Le nombre d'heures de RTT doit être positif").optional(),
  overtime_enabled: z.boolean().default(false),
  overtime_has_custom_convention: z.boolean().optional(),
  overtime_rate_36_to_43_percent: z.number().min(0).optional(),
  overtime_rate_above_43_percent: z.number().min(0).optional(),
  indemnities: z.array(remunerationIndemnityPayloadSchema),
});

/** Profile block for PATCH (partial). Cross-field rules validated in API when `profile` is sent. */
export const remunerationProfileUpdatePayloadSchema = remunerationProfileInsertPayloadSchema.partial();

export type RemunerationProfileInsertPayload = z.infer<typeof remunerationProfileInsertPayloadSchema>;
export type RemunerationProfileUpdatePayload = z.infer<typeof remunerationProfileUpdatePayloadSchema>;

/** Pay tracking read model: legacy shells may expose hourly_rate below SMIC (or 0). */
export const remunerationProfilePayTrackingReadSchema = remunerationProfileInsertPayloadSchema
  .omit({ hourly_rate: true })
  .extend({
    hourly_rate: z.number().min(0),
  });

export type RemunerationProfilePayTrackingReadPayload = z.infer<typeof remunerationProfilePayTrackingReadSchema>;

export const getRemunerationProfileParamsSchema = z.object({
  id: z.guid(),
});

/** GET /remuneration-profiles/:id — insert-shaped profile plus resolved origin for UI gating. */
export const getRemunerationProfileResponseSchema = remunerationProfileInsertPayloadSchema.extend({
  profile_origin: remunerationProfileOriginSchema,
});

export type GetRemunerationProfileResponse = z.infer<typeof getRemunerationProfileResponseSchema>;
